import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { industries, badgeLabel } from './industriesData';
import './UseCases.css';

export const UseCases = () => {
  const reduced = useReducedMotion();
  const [active, setActive] = useState('customer-support');
  const tabsRef = useRef(null);

  // Honour hash links from homepage industry cards
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && industries.find((i) => i.id === hash)) {
      setActive(hash);
    }
  }, []);

  // Scroll active tab into view when switching
  useEffect(() => {
    if (!tabsRef.current) return;
    const btn = tabsRef.current.querySelector(`[data-id="${active}"]`);
    if (btn) btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [active]);

  const panel = industries.find((i) => i.id === active);

  const fade = {
    hidden: { opacity: 0, y: reduced ? 0 : 18 },
    visible: (i = 0) => ({
      opacity: 1, y: 0,
      transition: { duration: reduced ? 0.2 : 0.55, delay: reduced ? 0 : i * 0.07, ease: [0.16, 1, 0.3, 1] },
    }),
  };

  return (
    <div className="uc-page">
      {/* Hero */}
      <section className="uc-hero">
        <div className="uc-hero-glow" aria-hidden="true" />
        <motion.h1 className="uc-title" initial="hidden" animate="visible" variants={fade}>
          Agents for every industry.
        </motion.h1>
        <motion.p className="uc-sub" initial="hidden" animate="visible" variants={fade} custom={1}>
          Pick your vertical to see the exact agents we build — and what they deliver.
        </motion.p>
      </section>

      {/* Industry tab bar */}
      <div className="uc-tabs-wrap">
        <div className="uc-tabs" ref={tabsRef} role="tablist" aria-label="Industry selector">
          {industries.map((ind) => {
            const Icon = ind.icon;
            return (
              <button
                key={ind.id}
                data-id={ind.id}
                className={`uc-tab ${ind.id === active ? 'active' : ''}`}
                role="tab"
                aria-selected={ind.id === active}
                onClick={() => setActive(ind.id)}
              >
                <Icon size={15} />
                {ind.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Panel */}
      <motion.section
        key={active}
        className="uc-panel"
        initial={{ opacity: 0, y: reduced ? 0 : 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: reduced ? 0.1 : 0.38, ease: [0.16, 1, 0.3, 1] }}
        role="tabpanel"
      >
        <div className="uc-panel-head">
          <p className="uc-panel-tagline">{panel.tagline}</p>
          {panel.outcome && (
            <span className="uc-panel-outcome">{panel.outcome}</span>
          )}
        </div>

        <div className="uc-agents">
          {panel.agents.map((agent, i) => (
            <motion.div
              key={agent.name}
              className="uc-agent"
              variants={fade}
              initial="hidden"
              animate="visible"
              custom={i}
            >
              <p className="uc-agent-name">{agent.name}</p>
              <p className="uc-agent-desc">{agent.desc}</p>
              <span className="uc-agent-badge">{badgeLabel[agent.type]}</span>
            </motion.div>
          ))}
        </div>

        <div className="uc-panel-cta">
          <Link to={`/contact?interest=${panel.id}`} className="uc-panel-cta-link">
            Build this for us <ArrowRight size={15} />
          </Link>
          <Link to={panel.solution} className="uc-panel-solution-link">
            See solution details <ArrowRight size={14} />
          </Link>
        </div>
      </motion.section>

      {/* CTA */}
      <section className="uc-cta">
        <div className="uc-cta-glow" aria-hidden="true" />
        <h2>Don't see your use case?</h2>
        <p>Tell us what you need. We'll scope it in 24 hours.</p>
        <Link to="/contact" className="btn-primary">Book a Scoping Call <ArrowRight size={18} /></Link>
      </section>
    </div>
  );
};
